import React from 'react'
import { useAuthContext } from '@eqworks/common-login'
import { routes } from '../../containers/app/route-config'

export const withAuthContext = (Component) => {
  const _Component = () => <Component authContext={useAuthContext()} />
  return _Component
}

export function getAccess() {
  let access = window.localStorage.access
  try {
    if (access) {
      return JSON.parse(access)
    }
  } catch (err) {
    console.error(err)
  }
  return null
}
export default getAccess

export function hasCustomerAccess(access = getAccess()) {
  return access.read > 0 || access.read === -1
}

export function hasFinAccess(access = getAccess()) {
  return [
    'wl',
    'customers',
    'read',
  ].every((k) => access[k] === -1) && (access.write === -1 || access.write >= 1000)
}

export function hasDataProvAccess(access = getAccess()) {
  return access.read === 500 && access.write === 10
}

// TODO:  1) integrate prefix into jwt.
//        2) ONLY require dev prefix for this function,
// so that only dev has to re-login, and keep other users compatible
export function hasDevAccess(access = getAccess()) {
  return access.prefix === 'dev'
}

export function hasInternalAccess(access = getAccess()) {
  return access.wl === -1 && access.customers === -1
}

export function hasAppReviewerAccess(access = getAccess()) {
  return access.prefix === 'appreviewer'
}

export function hasTesterAccess(access = getAccess()) {
  return access.prefix === 'tester'
}

const locusUserRoles = [
  { role: 'isCustomer', verify: hasCustomerAccess },
  { role: 'isFin', verify: hasFinAccess },
  { role: 'isDev', verify: hasDevAccess },
  { role: 'isDataProv', verify: hasDataProvAccess },
  { role: 'isInternal', verify: hasInternalAccess },
  { role: 'isAppReviewer', verify: hasAppReviewerAccess },
  { role: 'isTester', verify: hasTesterAccess },
]

/* locusSections will be ['map', 'layer', 'beacons', 'poi', 'segments',
    'builder', 'finance', 'intelligence', 'marketplace', 'hub', 'ql', 'notebook'] */
const rawLocusSections = routes.map(({ section }) => section)
const locusSections = rawLocusSections
  .filter((section, index) => rawLocusSections.indexOf(section) === index)

// only show ML to dev
// only show Hub to internal
// only show Finance to (isFin || isDataProvider)
// don't show poi or poi/:poi to isDataProvider
/* combinations include:
  allow everything - allowed: ['all'], restricted: ['none']
  allow everything except one - allowed: ['all'], restricted: ['ql']
  only allow one thing - allowed: ['ml'], restricted: ['all'] */

const sectionAccessByRole = [
  {
    role: 'isCustomer',
    routes: {
      allowed: ['all'], // sees everything except 4 things
      restricted: ['ql', 'hub', 'notebook', 'finance', 'widget-studio', 'ai-targeting', 'dooh-planner', 'dashboard'],
    },
  },
  {
    role: 'isFin',
    routes: {
      allowed: ['all'], // sees everything except 3 things
      restricted: ['ql', 'hub', 'notebook', 'widget-studio', 'ai-targeting', 'dooh-planner', 'dashboard'],
    },
  },
  {
    role: 'isDev',
    routes: {
      allowed: ['all'], // sees everything
      restricted: ['none'],
    },
  },
  {
    role: 'isDataProv',
    routes: {
      allowed: ['all'], // sees everything except 4 things
      restricted: ['poi', 'ql', 'hub', 'notebook', 'widget-studio', 'ai-targeting', 'dooh-planner', 'dashboard'],
    },
  },
  {
    role: 'isInternal',
    routes: {
      allowed: ['all'], // sees everything
      restricted: ['dashboard', 'dooh-planner', 'ai-targeting'],
    },
  },
  {
    role: 'isAppReviewer',
    routes: {
      allowed: ['segments', 'builder', 'poi', 'home'], // sees only 4 things
      restricted: ['all'],
    },
  },
  {
    role: 'isTester',
    routes: {
      allowed: ['all'], // sees everything except 1 thing
      restricted: ['finance', 'ai-targeting', 'dooh-planner', 'dashboard'],
    },
  },
]

export const checkUserRoles = (access) => locusUserRoles
  .filter(({ verify }) => Boolean(verify(access)))
  .map(({ role }) => role)

export const getVisibleRoutes = ({ userRoles, policies=[] }) => {
  // e.g. userRoles = [isDev, isFin, isInternal]
  const boundariesConfig = sectionAccessByRole.filter(({ role }) => userRoles.includes(role))

  const rawAllowedRoutes = []
  const rawRestrictedRoutes = []
  boundariesConfig.forEach(({ routes: { allowed, restricted } }) => {
    allowed.forEach((route) => {
      if (!rawAllowedRoutes.includes(route)) {
        rawAllowedRoutes.push(route)
      }
    })
    restricted.forEach((route) => {
      rawRestrictedRoutes.push(route)
    })
  })

  /* if user has 2 user roles, need to handle how to restrict routes
  so keep count how many times it shows up if allowed routes only contains all */
  const restrictedRouteCount = {}
  rawRestrictedRoutes.forEach((route) => {
    if (!restrictedRouteCount[route]) {
      restrictedRouteCount[route] = 1
    } else {
      restrictedRouteCount[route] += 1
    }
  })

  let trueRestrictedRoutes
  const hasOnlyAllInAllowedRoutes = rawAllowedRoutes.length === 1 && rawAllowedRoutes[0] === 'all'

  if (hasOnlyAllInAllowedRoutes) {
    const TIMES_FOR_RESTRICTION = userRoles.length
    trueRestrictedRoutes = (userRoles.length > 1)
      ? Object.entries(restrictedRouteCount)
        .filter(([route, count]) => count >= TIMES_FOR_RESTRICTION || route === 'none')
        .map(([route]) => route)
      : rawRestrictedRoutes
  } else {
    trueRestrictedRoutes = (userRoles.length > 1)
      ? Object.entries(restrictedRouteCount)
        .filter(([route]) => route !== 'all')
        .map(([route]) => route)
      : rawRestrictedRoutes
  }

  // none overrides any routes that are restricted
  const restrictedRoutes = (trueRestrictedRoutes.includes('none')) ? [] : trueRestrictedRoutes

  // all overrides any routes that were specified as allowed
  const allowedRoutes = (rawAllowedRoutes.includes('all')) ? locusSections : rawAllowedRoutes

  // regardless of user roles, if the user has a valid policies, then the user can access the rote
  const policyPrefix = policies.map((policy) => policy.split(':')[0])
  const visibleRoutes = allowedRoutes.filter((route) => !restrictedRoutes.includes(route)|| policyPrefix.includes(route))
  return visibleRoutes
}

export const checkPolicies = (targetPolicies, policies = []) => {
  return policies.some((p) => p.match(targetPolicies))
}
